'use strict';

import errorCodes from '../config/errors.json';

export default angular
  .module('maestro.services.utils', ['maestro.services.config'])
  .factory('utils', utils)
  .name;

function utils($filter,
               webcore,
               config,
               $q,
               $log,
               ToastsService,
               $injector) {

  const APP_NAME = config.getConfigByKey('environment.appName');
  const PHONE_TYPES = config.getConfigByKey('phoneTypes');

  const APP_VERSION = VERSION; // webpack create global variable for that
  const emailRegexp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const $t = $filter('translate');

  const durationFormats = {
    shortSec: 'w[w] d[d] h[h] m[m] s[s]',
    sec: 'w [w] d [d] h [hrs] m [min] s [sec]',
    shortMin: 'w[w] d[d] h[h] m[m]',
    min: 'w [w] d [d] h [hrs] m [min]',
  };

  return {

    getAppName: function() {
      return APP_NAME;
    },

    getAppVersion: function() {
      return APP_VERSION;
    },

    clearCache: function() {
      const preservedValues = [
        'Main-Language',
        'CurrentBrand',
        'Main-Factor',
        /Auth-UserSignaturePrivateKey.+/
      ]
        .reduce((acc, key) => {
          if (typeof key === 'string') {
            acc.push({
              key: key,
              value: this.getStorageItem(key)
            });
          } else {
            acc = acc.concat(this.getStorageItemsByRegexp(key));
          }
          return acc;
        }, []);

      webcore.services.localStorage.clear();

      preservedValues
        .forEach(({ key, value }) => {
          this.setStorageItem(key, value);
        });
    },

    getCookieExpires: function() {
      return new Date(new Date().getTime() + 31536e6); //365*24*3600*100
    },

    getBrowserTimezone: function() {
      let d = new Date();
      return 'GMT' + d.toString().match(/[\+|-]\d{4}/);
    },

    getApiKey: function() {
      return webcore.services.auth.apiKey;
    },

    /**
     * Check if user has all permissions listed
     * @param {Object} user
     * @param {number[]} permissions
     * @return {boolean}
     */
    checkUserPermission: function(user, permissions) {
      if (!permissions || !permissions.length || !user || !user.permission || !user.permission.length) {
        return false;
      }
      return permissions.every(p => user.permission.some(up => up.toString() === p.toString()));
    },

    /**
     * Check if user is some kind of admin or not
     * @param user
     * @return {boolean}
     */
    checkAdminRights: function(user) {
      return !!user.role
        || !!user.roleId
        || this.checkUserPermission(user, [3])
        || this.checkUserPermission(user, [0, 2]);
    },

    compareObjects: function(obj1, obj2, properties) {
      if (!obj1 || !obj2 || !properties) {
        return false;
      }
      for (let i = 0; i < properties.length; i++) {
        if (obj1[properties[i]] !== obj2[properties[i]]) {
          return false;
        }
      }
      return true;
    },

    /**
     * Compare two objects if one of them is edited by web form (assume that null, undefined and empty string is the same value)
     * @param {Object} obj1
     * @param {Object} obj2
     * @param {string[]} properties list of properties to check
     * @return {boolean}
     */
    compareFormObjects: function(obj1, obj2, properties) {
      if (!obj1 && !obj2) {
        return true;
      }
      if ((obj1 && !obj2) || (!obj1 && obj2)) {
        return false;
      }
      if (!properties) {
        // unique array of all fields on both objects
        properties = [...new Set(Object.keys(obj1).concat(Object.keys(obj2)))];
      }

      return !properties.some(p => {
        let f1 = obj1[p];
        let f2 = obj2[p];
        let isF1Empty = f1 == null || f1.length === 0; // Check for undefined, null, [], ""
        let isF2Empty = f2 == null || f2.length === 0;
        if (isF1Empty && isF2Empty) {
          return false;
        } else if ((isF1Empty && !isF2Empty) || (!isF1Empty && isF2Empty)) {
          return true;
        }
        return !angular.equals(f1, f2);
      });
    },

    getStorageItem: function(key) {
      return webcore.services.localStorage.get(key);
    },

    getStorageItemsByRegexp: function(regexp) {
      return webcore.services.localStorage.getByRegexp(regexp);
    },

    setStorageItem: function(key, value) {
      if (angular.isUndefined(value)) {
        // to avoid saving 'undefined' string
        webcore.services.localStorage.remove(key);
        return;
      }
      webcore.services.localStorage.set(key, value);
    },

    removeStorageItem: function(key) {
      webcore.services.localStorage.remove(key);
    },

    /**
     * Constructs the url to the webapp host for the specified brand, based upon the servers config.
     * Including 'http(s)://' prefix.
     * @param {string} [brand] optional brand
     * @returns {Promise<string>}
     */
    getWebappHostUrl: function(brand) {
      if (!brand) {
        return $q.when(webcore.services.cloudConfig.getBaseUrl());
      }
      return $q.when(webcore.services.cloudConfig.getCurrentCloud())
        .then(cloud => {
          if (cloud && cloud.servers) {
            let server = cloud.servers.find(server => server.type === 'webapp' && server.brand === brand);
            if (server) {
              return (server.ssl ? 'https://' : 'http://') +
                server.host +
                (server.port ? ':' + server.port : '');
            }
          }
          return $q.when(webcore.services.cloudConfig.getBaseUrl());
        });
    },

    getWebappHostUrlSync: function() {
      return webcore.services.cloudConfig.baseUrl;
    },

    getServerTimezone: function() {
      return $q.when(webcore.services.cloudConfig.getCurrentCloud())
        .then((c) => {
          if (!c) {
            return;
          }
          return c.defaultTimezone;
        });
    },

    /**
     * Get organization Logo path
     * @param {string} [orgId] Organization ID. If empty, PPC logo will be used
     * @returns {Promise<string>}
     */
    getOrganizationLogoPath: function(orgId) {
      if (!orgId) {
        return $q.resolve('/assets/images/care-daily-logo.png');
      }
      return this.getWebappHostUrl()
        .then(baseUrl => {
          return `${baseUrl}/admin/json/organizations/${orgId}/objects/logo`;
        });
    },

    /**
     * Get child organizations if parent has one
     * @param {Array<{}>} organizations All available Organizations array
     * @param {number} parentId Parent organization ID to search children for
     * @returns {Array<{}>}
     */
    getChildOrganizations: function(organizations, parentId) {
      let childOrgs = [];
      if (!organizations || organizations.length <= 1) {
        return childOrgs;
      }
      childOrgs = organizations.filter((org) => org.parentId === parentId);
      return childOrgs;
    },

    getChallengeType: function(c) {
      if (!c) {
        return '';
      }
      let type = ''.concat(c.participantType, c.challengeType, c.winnerType || 0, c.recurring);
      if (type == '1113') {
        return '1110';
      } else if (type == '0113') {
        return '0110';
      } else if (type == '1400' && c.template == true) {
        return '14001';
      } else {
        return type;
      }
    },

    ngDigest: function($scope) {
      if (!($scope.$$phase == '$apply' || $scope.$$phase == '$digest')) {
        $scope.$digest();
      }
    },

    nextMonth: function(date) {
      let d = date || new Date();
      return new Date(d.getFullYear(), d.getMonth() + 1, 1);
    },

    preMonth: function(date) {
      let d = date || new Date();
      return new Date(d.getFullYear(), d.getMonth() - 1, 1);
    },

    /**
     * Test 'items' array for specific key:value pair.
     * @param {Array<Object>} items
     * @param {string} key
     * @param {string|number|boolean} value
     * @returns {number} index
     */
    inArray: function(items, key, value) {
      if (Array.isArray(items) && value != null) {
        return items.findIndex(el => el[key] == value);
      }
      return -1;
    },

    uniqueArray: function(arr, key) {
      if (arr == null || arr.length == 0) {
        return [];
      }
      let newArray = new Array();
      for (let item of arr) {
        if (this.inArray(newArray, key, item[key]) == -1) {
          newArray.push(item);
        }
      }
      return newArray;
    },

    recipientUserName: function(user) {
      let str = '';
      if (!user) {
        return str;
      }
      if (user.firstName) {
        str += user.firstName;
      }
      if (user.lastName) {
        str += ' ' + user.lastName;
      }
      if (str == '') {
        str += user.email ? user.email.email : '';
      }
      return str;
    },

    recipients: function(message) {
      let userRecipientFun = this.recipientUserName;
      if (!message.recipients) {
        return '';
      }
      let orgs = [],
        groups = [],
        users = [];
      let orgsStr = '',
        groupsStr = '',
        usersStr = '';
      message.recipients.forEach(function(item, index) {
        if (item.groupId) { // Group
          groups.push(item);
        } else if (item.userId) { // User
          item.nameStr = userRecipientFun(item);
          users.push(item);
        } else if (item.organizationId) { // Organization
          orgs.push(item);
        }
      });
      orgs.sort(function(a, b) {
        return a.organizationName.localeCompare(b.organizationName);
      });
      groups.sort(function(a, b) {
        return a.groupName.localeCompare(b.groupName);
      });
      users.sort(function(a, b) {
        return a.nameStr.localeCompare(b.nameStr);
      });
      orgs.forEach(function(item, index) {
        orgsStr += item.organizationName + ', ';
      });
      groups.forEach(function(item, index) {
        groupsStr += item.groupName + ', ';
      });
      users.forEach(function(item, index) {
        usersStr += item.nameStr + ', ';
      });

      return {
        'orgsStr': orgsStr == '' ? '' : orgsStr.substr(0, orgsStr.length - 2),
        'groupsStr': groupsStr == '' ? '' : groupsStr.substr(0, groupsStr.length - 2),
        'usersStr': usersStr == '' ? '' : usersStr.substr(0, usersStr.length - 2),
        'orgs': orgs,
        'groups': groups,
        'users': users
      };

    },

    /**
     * Escapes a string for HTML interpolation.
     */
    escapeHtml: function(string) {
      // List of HTML entities for escaping.
      const htmlEscapes = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        '\'': '&#x27;',
        '/': '&#x2F;'
      };
      // Regex containing the keys listed immediately above.
      const htmlEscaper = /[&<>"'\/]/g;

      return ('' + string).replace(htmlEscaper, match => htmlEscapes[match]);
    },

    getMessageText: function(message) {
      let index = message.text.indexOf('<![CDATA[');
      return message.text.substring(0, index);
    },

    getMessageData: function(message) {
      let index = message.text.indexOf('<![CDATA[');
      let length = message.text.length;
      return message.text.substring(index + 9, length - 3);
    },

    hasFeature: function(org, f) {
      if (!org || !f || f == '') {
        return false;
      }
      let features = org.features || '';
      return features.indexOf(f) != -1;
    },

    /**
     * Transform Array into Object
     * @param {Array{}} arr
     * @param {string|number} key
     * @returns {} Object{key => {}}
     */
    array2Map: function(arr, key) {
      let resultMap = new Map();
      if (!arr || arr.length == 0) {
        return resultMap;
      }
      for (let item of arr) {
        resultMap.set(item[key], item);
      }
      return resultMap;
    },

    csv2Arr: function(csvText) {
      let lines = csvText.split('\n');
      let result = [];
      let headers = lines[0].split(',');
      for (let i = 1; i < lines.length - 1; i++) {
        let obj = {};
        let currentline = lines[i].split(',');
        for (let j = 0; j < headers.length; j++) {
          obj[headers[j]] = currentline[j];
        }
        result.push(obj);
      }
      return result;
    },

    updateLogo: function(logoPath) {
      let element = $('.main.logo');
      let defaultImage = element.attr('fallback-bg-image') || '/assets/images/care-daily-logo.png';
      if (!logoPath) {
        element.css('background-image', `url(${defaultImage})`);
      } else {
        let image = new Image();
        image.src = logoPath;
        image.onload = function() {
          // Image loaded, set the background image to it
          element.css('background-image', `url(${logoPath}?ts=${new Date().getTime()})`);
        };
        image.onerror = function() {
          // Image failed to load - use default
          element.css('background-image', `url(${defaultImage})`);
        };
      }
    },

    updateOrgCssFile: function(arr) {
      let colorLuminance = function(hex, lum = 0, opacity = 1) {
        // validate hex string
        hex = String(hex).replace(/[^0-9a-f]/gi, '');
        if (hex.length < 6) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        // convert to decimal and change luminosity
        let rgba = [],
          c, i;
        for (i = 0; i < 3; i++) {
          c = parseInt(hex.substr(i * 2, 2), 16);
          c = Math.round(Math.min(Math.max(0, c + (c * lum)), 255));
          rgba.push(c);
        }
        rgba.push(opacity);
        return 'rgba(' + rgba.join(',') + ')';
      };
      let flags = {};
      let backgroundVal = this.getPropertyByName(arr, 'background', true);
      if (backgroundVal && backgroundVal != '') {
        this.updateStyleInnerHtml('background.min.css', [
          {
            name: 'background',
            value: backgroundVal
          }
        ]);
      } else {
        this.removeStyleInnerHtml('background.min.css');
      }

      let secondaryColorVal = this.getPropertyByName(arr, 'secondaryColor', true);
      if (secondaryColorVal && secondaryColorVal != '') {
        flags.secondaryColor = true;
        let secondaryDarkColor = colorLuminance(secondaryColorVal, -0.2);
        let secondaryHoverColor = colorLuminance(secondaryColorVal, 0, 0.95);
        this.updateStyleInnerHtml('secondary.min.css', [
          {
            name: 'secondaryColor',
            value: secondaryColorVal
          }, {
            name: 'secondaryDarkColor',
            value: secondaryDarkColor
          }, {
            name: 'secondaryHoverColor',
            value: secondaryHoverColor
          }
        ]);
      } else {
        this.removeStyleInnerHtml('secondary.min.css');
      }

      let primaryColorVal = this.getPropertyByName(arr, 'primaryColor', true);
      if (primaryColorVal && primaryColorVal != '') {
        flags.primaryColor = true;
        let primaryLightColor = colorLuminance(primaryColorVal, 0, 0.25);
        let primaryLighterColor = colorLuminance(primaryColorVal, 0, 0.05);
        let primaryDarkColor = colorLuminance(primaryColorVal, -0.1);
        this.updateStyleInnerHtml('primary.min.css', [
          {
            name: 'primaryColor',
            value: primaryColorVal
          }, {
            name: 'primaryLightColor',
            value: primaryLightColor
          }, {
            name: 'primaryLighterColor',
            value: primaryLighterColor
          }, {
            name: 'primaryDarkColor',
            value: primaryDarkColor
          }
        ]);
      } else {
        this.removeStyleInnerHtml('primary.min.css');
      }
    },

    removeStyleInnerHtml: function(filename) {
      let removeStyle = function(filename) {
        let id = filename.replace(/\..*/, '') + '-style';
        let $e = $('#' + id)[0];
        if ($e) {
          $e.remove();
        }
      };
      removeStyle(filename);
    },

    updateStyleInnerHtml: function(filename, arr) {
      let id = filename.replace(/\..*/, '') + '-style';
      arr = arr.sort(function(a, b) {
        return a.name.length < b.name.length;
      });
      $.get('/styles/branding/' + filename, function(css) {
        for (let p of arr) {
          let name = 'org' + p.name,
            value = p.value,
            regExp = new RegExp(name, 'ig');
          css = css.replace(regExp, value);
        }
        let $e = $('#' + id)[0];
        if ($e) {
          $e.remove();
        }
        let sheet = document.createElement('style');
        sheet.id = id;
        sheet.innerHTML = css;
        document.body.appendChild(sheet);

      });
    },

    /**
     * Get formatted address string for location, organization or other address aware model
     * @param {{addrStreet1: string, addrStreet2: string, addrCity: string, zip: string, state?: {abbr?: string, name?: string}, country?: {countryCode?: string, countryName?: string}}} location address aware model
     * @returns {string} formatted address string
     */
    generateAddress: function(location) {
      if (!location) {
        return '';
      }

      return [
        location.addrStreet1,
        location.addrStreet2,
        location.addrCity,
        location.state?.abbr || location.state?.name,
        location.country?.countryCode || location.country?.name,
        location.zip,
      ]
        .filter(e => !!e?.trim())
        .join(', ');
    },

    handleApiError: function(error) {
      const unknownError = errorCodes.unknown;
      if (error) {
        $log.error(error);
      }

      if (!error) {
        ToastsService.error($t(unknownError.title), $t(unknownError.desc));
      } else if (error.resultCode != null) {
        // If it is server-side internal error
        let predefinedError = errorCodes[error.resultCode];
        if (predefinedError) {
          if (error.resultCode === 0 || error.resultCode === 17) {
            // Not an error actually
            ToastsService.info($t(predefinedError.title), $t(predefinedError.desc));
          } else {
            const errorMsg = $t(predefinedError.desc) || error.resultCodeMessage;
            ToastsService.warning($t(predefinedError.title), errorMsg);
          }
        } else if (error.resultCodeDesc || error.resultCodeMessage) {
          ToastsService.warning(error.resultCodeDesc, error.resultCodeMessage);
        } else {
          ToastsService.error($t(unknownError.title), $t(unknownError.desc));
        }
      } else if (error.response && error.response.status != null) {
        // Some HTTP error with status code
        // see also error.response.statusText
        ToastsService.error($t(unknownError.title), $t(unknownError.desc));
      } else {
        // Unexpected network error
        ToastsService.error($t(unknownError.title), $t(unknownError.desc));
      }
    },

    isOrgAllowedType: function(typeId, org) {
      if (!org.deviceTypes || org.deviceTypes == '') {
        return false;
      }
      let arr = org.deviceTypes.split(',');
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] == typeId) {
          return true;
        }
      }
      return false;
    },

    getOrgAllowedDeviceTypes: function(allDeviceTypes, org, parentOrg) {
      allDeviceTypes = allDeviceTypes
        .filter(dt => {
          // Exclude "nonOrganization" device types (device types that was "hidden" by server)
          // SEE comments in CC-753
          return !dt.attributes || !dt.attributes.some(a => a.name === 'nonOrganization' && a.value == '1');
        });

      if (org && (typeof org.parentId == 'undefined' || !org.parentId)) {
        return allDeviceTypes;
      }
      if (!parentOrg || !parentOrg.deviceTypes || parentOrg.deviceTypes == '') {
        return allDeviceTypes;
      } else {
        let orgAllowedDeviceTypeIds = parentOrg.deviceTypes.split(',');
        return allDeviceTypes.filter((deviceType) =>
          orgAllowedDeviceTypeIds.some(orgAllowedDeviceTypeId => orgAllowedDeviceTypeId === deviceType.id.toString()));
      }
    },

    convertKeysToItems: function(allItems, keys, propertyName) {
      if (!keys || keys.length == 0) {
        return [];
      }
      let arr = [];
      for (let key of keys) {
        for (let item of allItems) {
          if (key == item[propertyName]) {
            arr.push(item);
          }
        }
      }
      return arr;
    },

    appendAttr2DeviceType: function(deviceType) {
      if (!deviceType) {
        return;
      }
      deviceType.iconUrl = this.getDeviceTypeIconUrl(deviceType);
      deviceType.deviceListIcon = this.getDeviceTypeIconName(deviceType);
      deviceType.defaultDescription = deviceType.attributes?.find(a => a.name === 'defaultDescription')?.value;
      deviceType.dependencyDeviceTypes = deviceType.attributes?.find(a => a.name === 'dependencyDeviceTypes')?.value;
    },

    getDeviceTypeIconName(deviceType) {
      return deviceType?.attributes?.find(a => a.name === 'deviceListIcon')?.value;
    },

    getDeviceTypeIconUrl(deviceType) {
      if (!deviceType) {
        return;
      }
      let attr = (deviceType.attributes || []).find(a => a.name === 'deviceListIcon');
      let imageDomain = this.getWebappHostUrlSync();
      if (attr && attr.value) {
        return imageDomain + '/img/icons/icon-' + attr.value + '.png';
      } else {
        return imageDomain + '/templates/' + deviceType.id + '/thumbnail.png';
      }
    },

    getPropertyByName: function(orgProperties, paramName, inheritance) {
      if (!(orgProperties && orgProperties.length > 0)) {
        return null;
      }
      let parentVal, val;
      for (let i = 0; i < orgProperties.length; i++) {
        let p = orgProperties[i];
        if (p.name == paramName) {
          if (p.parent == true) {
            parentVal = p.value;
          } else {
            val = p.value;
          }
          if (parentVal && val) {
            break;
          }
        }
      }
      if (inheritance) {
        return val ? val : parentVal;
      } else {
        return val;
      }
    },

    isEmail(email) {
      return emailRegexp.test(email);
    },

    emailRegexp() {
      return emailRegexp;
    },

    getOrgInfo: function(organizationId, organizations) {
      if (!organizations || organizations.length === 0) {
        return;
      }
      let result = organizations.find(function(element) {
        return element.id === organizationId;
      });
      return result;
    },

    getBrandName: function(organizationId, organizations) {
      let currentOrg = this.getOrgInfo(organizationId, organizations);
      if (currentOrg && currentOrg.brand) {
        return currentOrg.brand;
      }
      if (currentOrg && currentOrg.parentId) {
        let parentOrg = this.getOrgInfo(currentOrg.parentId, organizations);
        return parentOrg.brand;
      }
      return;
    },

    /**
     * Convert number of minutes in `hh:mm` format
     * @param {number} minutes
     * @return {string}
     */
    convertMinutes(minutes) {
      let h = Math.floor(minutes / 60);
      let m = minutes % 60;
      h = h < 10 ? '0' + h : h;
      m = m < 10 ? '0' + m : m;
      return `${h}:${m}`;
    },

    /**
     * Group by particular date of the month
     * @param {Array<Object>} list
     * @param {string} paramName
     * @param {string} [timezoneId] Timezone Id. GMT by default
     * @return {Array<{date: Date, collection: Object[]}>}
     */
    groupByDateParam(list, paramName, timezoneId) {
      let result = [];
      if (!list || !list.length) {
        return result;
      }
      timezoneId = timezoneId || 'GMT';

      let groupedObj = list.reduce((resultObj, element) => {
        if (element[paramName]) {
          let dayOfMonth = moment(element[paramName]).tz(timezoneId).startOf('day').valueOf();
          resultObj[dayOfMonth] = resultObj[dayOfMonth] || [];
          resultObj[dayOfMonth].push(element);
          return resultObj;
        }
        // TODO
        // now skip
      }, {});

      for (let prop in groupedObj) {
        if (groupedObj.hasOwnProperty(prop)) {
          let block = {
            date: new Date(parseInt(prop)),
            collection: groupedObj[prop]
          };
          result.push(block);
        }
      }
      return result;
    },

    parsePropertyString(value) {
      let result = value.replace(/.*\./, '');
      if (result.includes('_')) {
        result = result.toLowerCase().replace(/_/g, ' ');
      } else {
        result = result.replace(/([a-z])([A-Z])/g, '$1 $2');
      }
      result = result.replace(/\b[a-z]/g, char => char.toUpperCase());
      return result;
    },

    parsePythonString(value) {
      if (!value || typeof value !== 'string') {
        return;
      }
      let result = value.replace(/_([a-z])/g, function(m, w) {
        return ' ' + w.toUpperCase();
      });
      return result.charAt(0).toUpperCase() + result.slice(1);
    },

    deviceParameterToString(parameter, value, index, locale) {
      if (!parameter || value == null) {
        return value || '–';
      }

      if (parameter.displayInfo?.options?.length > 0) {
        let options = parameter.displayInfo.options;

        // Ranged Parameter
        if (parameter.displayInfo.ranged) {
          /*
           * NOTE: Server should sort it by option.value (or option.id which is the same sort order).
           * For current value find the next greater value from options list.
           */
          let i = options.findIndex(o => parseFloat(value) <= parseFloat(o.value));
          // Take the last option if value is greater then greater options
          i = i === -1 ? options.length - 1 : i;

          let optionSelected = options[i];
          if (optionSelected?.mlName?.[locale]) {
            return `${optionSelected.mlName[locale]} / ${this.formatDeviceParameterValue(parameter, value, locale)}`;
          }
        } else {
          let optionSelected = options.find(o => {
            if (index >= 0) { // If parameter has index
              return o.value === value && o.index === index;
            }
            return o.value == value;
          });
          if (optionSelected && optionSelected.mlName && optionSelected.mlName[locale]) {
            return optionSelected.mlName[locale];
          }
        }
      }

      return this.formatDeviceParameterValue(parameter, value, locale);
    },

    getDeviceParameterIcon(parameter, value) {
      if (parameter?.displayInfo?.options?.length > 0) {
        let options = parameter.displayInfo.options;
        let optionSelected;

        if (parameter.displayInfo.ranged) {
          // Ranged parameter
          value = parseInt(value, 10);
          let i = options.findIndex(o => value <= parseFloat(o.value));
          i = i === -1 ? options.length - 1 : i;
          optionSelected = options[i];
        } else {
          optionSelected = options.find(o => o.value == value);
        }

        if (optionSelected && optionSelected.iconFont && optionSelected.icon) {
          return optionSelected.iconFont + ' fa-' + optionSelected.icon;
        }
      }
      if (!!parameter.displayInfo?.icon) {
        return `${parameter.displayInfo.iconFont || 'fal'} fa-${parameter.displayInfo.icon}`;
      }

      return 'fal fa-heart-rate';
    },

    formatDeviceParameterValue(parameter, value, locale) {
      if (!parameter || value == null) {
        return value;
      }
      if (parameter.systemUnit && !['string', 'int', 'boolean', 'money'].includes(parameter.systemUnit)) {
        if (parameter.systemUnit === 'degrees C') {

          // Exception for the delta
          if (parameter.name === 'heatCoolMinDelta') {
            return `${value} °C`;
          }

          let oldValue = value;
          value = this.convertCToF(parseFloat(value));
          value = Math.round(value * 100) / 100;
          return `${oldValue} °C ‥ ${value} °F`;
        }
        if (parameter.systemUnit === 'degrees F') {
          return `${value} °F`;
        }
        return `${value} ${parameter.systemUnit}`;
      }
      if (parameter.systemMultiplier === '%') {
        return `${value}%`;
      }
      return value;
    },

    convertPhoneToNumber(value) {
      if (!value || typeof value !== 'string') {
        return;
      }
      let result = value.replace(/[^\w]/g, '');
      result = isFinite(result) && parseInt(result, 10);
      return result;
    },

    /* Returns billing system parameters */
    generateBillingPath(objectsArr) {
      if (!objectsArr || objectsArr.length === 0) {
        return;
      }
      let billingSystem;
      let billingMerchant;
      for (const value of objectsArr) {
        if (value.name === 'billingSystemId') {
          let systemsArr = config.getConfigByKey('billingSystems');
          billingSystem = systemsArr.find(el => el.id === parseInt(value.value));
        } else if (value.name === 'billingMerchant') {
          billingMerchant = value.value;
        }
      }
      if (billingSystem && billingMerchant) {
        // Merchant ID will go as prefix or suffix to path
        let prefix = billingSystem.prefix ? billingMerchant + '.' : '';
        let suffix = !billingSystem.prefix ? billingMerchant + '/' : '';
        billingSystem.url = 'https://' + prefix + billingSystem.path + suffix + 'subscriptions/';
        if (billingSystem.sandbox) {
          billingSystem.sboxUrl = 'https://' + prefix + billingSystem.sandbox + suffix + 'subscriptions/';
        }
      }
      return billingSystem;
    },

    /* Compare arrays, returns true if they're equal */
    compareArrays(range1, range2) {
      if (!angular.isArray(range1) || !angular.isArray(range2)) {
        return range1 === range2;
      }
      return range1.length === range2.length && range1.every((value, index) => value === range2[index]);
    },

    /**
     * Merge arrays of objects by properties.
     * @param {Array<{}>} target
     * @param {Array<{}>} source
     * @param {string[]} props
     */
    mergeByProperty(target, source, props) {
      source.forEach(sourceElement => {
        let targetElement = target.find(targetElement => {
          return !props.some(value => sourceElement[value] !== targetElement[value]);
        });
        targetElement ? Object.assign(targetElement, sourceElement) : target.push(sourceElement);
      });
    },

    /**
     * Generate new RFC4122 UUID (aka GUID)
     * It's not super-right, but it is okay for non-frequent usage
     * @see {https://www.ietf.org/rfc/rfc4122.txt}
     * @return {string} UUID
     */
    generateUUID() {
      if (crypto && crypto.getRandomValues && Uint8Array) {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
          (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
      } else {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
          .replace(/[xy]/g, function(c) {
            let r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
      }
    },

    getUserNameOrEmail(user) {
      let str = '';
      if (!user) {
        return;
      }
      if (user.firstName) {
        str += user.firstName;
      }
      if (user.lastName) {
        str += ' ' + user.lastName;
      }
      if (!str) {
        str = user.email ? user.email.email : '';
      }
      if (!str) {
        str = user.username;
      }
      if (!str) {
        str = $t('common.unknownUser');
      }
      return str.trim();
    },

    /**
     * Get translation if exists. If not - return default value
     * @param {string} translationKey translation key
     * @param {string} [defaultValue] default value (undefined by default)
     * @return {string|undefined}
     */
    translateOrDefault(translationKey, defaultValue) {
      let result = $t(translationKey);
      return result === translationKey ? defaultValue : result;
    },

    /**
     * Returns a function, that, as long as it continues to be invoked, will not be triggered.
     * The function will be called after it stops being called for N milliseconds.
     * @param func
     * @param {number} wait
     * @param {boolean} [immediate] If `immediate` is passed, trigger the function on the leading edge, instead of the trailing.
     * @return {*}
     */
    debounce(func, wait, immediate) {
      let timeout;
      return function() {
        let context = this, args = arguments;
        let later = function() {
          timeout = null;
          if (!immediate) {
            func.apply(context, args);
          }
        };
        let callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) {
          func.apply(context, args);
        }
      };
    },

    /**
     * Unpack bitmask.
     *
     * Server usually use bitmask to encode multiple values into single number.
     *
     * Bitmask form: b1 * 2**0 + b2 * 2**1 + b3 * 2**2 + b4 * 2**3 + ...
     *   Where b1, b2, ..., bn - are option-dependent boolean flags: 0 for disabled option and 1 for enabled
     *
     * For example we have set of possible options: [0, 1, 2, 3, 4, 5, 6]
     * So let's encode values `[0, 3, 4]` into single number:
     *   `2**0 + 2**3 + 2**4 // === 25`
     *
     * This function will invert this operation:
     *   `unpackBitmask(25); // === [0, 3, 4]`
     *
     * @param {number} bitmask
     * @return {Array<number>}
     */
    unpackBitmask(bitmask) {
      if (!angular.isNumber(bitmask)) {
        return [];
      }

      let result = [];
      let n = 0;
      while ((bitmask >> n) > 1) {
        n++;
      }

      while (n >= 0) {
        if (bitmask >> n === 1) {
          result.push(n);
          bitmask = bitmask - 2 ** n;
        }
        n--;
      }
      result.sort();

      return result;
    },

    /**
     * Pack values to bitmask.
     *
     * Server usually use bitmask to encode multiple values into single number.
     *
     * Bitmask form: b1 * 2**0 + b2 * 2**1 + b3 * 2**2 + b4 * 2**3 + ...
     *   Where b1, b2, ..., bn - are option-dependent boolean flags: 0 for disabled option and 1 for enabled
     *
     * For example we have set of possible options: [0, 1, 2, 3, 4, 5, 6]
     * So let's encode values `[0, 3, 4]` into single number:
     *   `2**0 + 2**3 + 2**4 // === 25`
     *
     * This function will invert do this operation
     *   `unpackBitmask([0, 3, 4]); // === 25`
     *
     * @param {Array<number>} values
     * @return {number}
     */
    packValuesToBitmask(values) {
      return values.reduce((sum, el) => sum + 2 ** el, 0);
    },

    /**
     * Cron string to human-readable converter
     * @param {string} cronString
     * @param {string} [locale]
     */
    parseCron(cronString, locale) {
      if (!cronString) {
        return;
      }

      // Code here is highly specific for cRonstrue framework
      let I18nService = $injector.get('I18nService');

      locale = locale || I18nService.getLocale()?.code || 'en';
      if (locale === 'zh') {
        locale = 'zh_CN';
      }
      let supportedLocales = [ // locales supported by cRonstrue
        'en',
        'cs',
        'es',
        'da',
        'de',
        'fi',
        'fr',
        'fa',
        'he',
        'it',
        'ja',
        'ko',
        'nb',
        'nl',
        'pl',
        'pt_BR',
        'ro',
        'ru',
        'sk',
        'sl',
        'sw',
        'sv',
        'tr',
        'uk',
        'zh_CN',
        'zh_TW'
      ];
      if (!supportedLocales.includes(locale)) {
        locale = supportedLocales[0];
      }
      try {
        return cronstrue.toString(cronString, {
          locale: locale,
          use24HourTimeFormat: !this.is12Hour(),
          dayOfWeekStartIndexZero: false,
          throwExceptionOnParseError: true
        });
      } catch (e) {
        $log.warn(`Unable to parse CRON string: "${cronString}" for "${locale}" locale`);
        $log.warn(e);
        return cronString;
      }
    },

    /**
     * Check if current user locale use 12 hour system (true) or 24-hour (false)
     * @return {boolean}
     */
    is12Hour() {
      return true;
      // try {
      //   let I18nService = $injector.get('I18nService');
      //   let locale = I18nService.getLocale()?.code || 'en';
      //   return !!new Intl.DateTimeFormat(locale, { hour: 'numeric' }).format(0).match(/\s/);
      // } catch (e) {
      //   return true;
      // }
    },

    timeDifference(startDate, endDate, format) {
      let units = format === 'minutes' || format === 'hours' || format === 'days' ? format : 'seconds';
      return moment(endDate).diff(moment(startDate), units, true);
    },

    /**
     * Create trend function using Simple Linear Regression and calculate the growth (fall) since the beginning of the period as a percentage
     * @param {Array<{x: number, y: number}>} dataset
     * @return {number}
     */
    simpleLinearRegressionTrendGrow(dataset) {
      if (!dataset || dataset.length <= 0) {
        return 0;
      }

      dataset = dataset
        .slice()
        .sort((a, b) => a.x - b.x);

      // the trend function will be `y = `, where `x` is dataset index and `y` is trend value
      let trendFunc = this.simpleLinearRegressionTrend(dataset);

      let firstX = dataset[0].x;
      let lastX = dataset[dataset.length - 1].x;

      return 100 * (trendFunc(lastX) - trendFunc(firstX)) / trendFunc(firstX);
    },

    /**
     * Create trend function using Polynomial Regression and calculate the growth (fall) since the beginning of the period as a percentage
     * @param {Array<{x: number, y: number}>} dataset
     * @return {number}
     */
    polynomialRegressionTrendGrow(dataset) {
      if (!dataset || dataset.length <= 0) {
        return 0;
      }

      dataset = dataset
        .slice()
        .sort((a, b) => a.x - b.x);

      let trendFunc = this.polynomialRegressionTrend(dataset);

      let firstX = dataset[0].x;
      let lastX = dataset[dataset.length - 1].x;

      return 100 * (trendFunc(lastX) - trendFunc(firstX)) / trendFunc(firstX);
    },

    /**
     * Get trend function for dataset using Simple Linear Regression
     * @param {Array<{x: number, y: number}>} dataset
     * @return {function(x: number):number}
     */
    simpleLinearRegressionTrend(dataset) {
      if (!dataset || dataset.length <= 0) {
        return () => 0;
      }

      let count = 0;
      let sumX = 0;
      let sumX2 = 0;
      let sumXY = 0;
      let sumY = 0;

      dataset.forEach(({ y, x }) => {
        count++;
        sumX += x;
        sumX2 += x * x;
        sumXY += x * y;
        sumY += y;
      });

      let det = count * sumX2 - sumX * sumX;
      let offset = (sumX2 * sumY - sumX * sumXY) / det;
      let scale = (count * sumXY - sumX * sumY) / det;

      return (x) => offset + x * scale;
    },

    /**
     * Get trend function for dataset using Polynomial Regression
     * @param {Array<{x: number, y: number}>} dataset
     * @return {function(x: number):number}
     */
    polynomialRegressionTrend(dataset) {
      if (!dataset || dataset.length <= 0) {
        return () => 0;
      }

      let count = 0;
      let sumX = 0;
      let sumX2 = 0;
      let sumX3 = 0;
      let sumX4 = 0;
      let sumY = 0;
      let sumXY = 0;
      let sumX2Y = 0;

      dataset.forEach(({ y, x }) => {
        count++;
        sumX += x;
        sumX2 += x * x;
        sumX3 += x * x * x;
        sumX4 += x * x * x * x;
        sumY += y;
        sumXY += x * y;
        sumX2Y += x * x * y;
      });

      let det = count * sumX2 * sumX4 - count * sumX3 * sumX3 - sumX * sumX * sumX4 + 2 * sumX * sumX2 * sumX3 - sumX2 * sumX2 * sumX2;
      let offset = sumX * sumX2Y * sumX3 - sumX * sumX4 * sumXY - sumX2 * sumX2 * sumX2Y + sumX2 * sumX3 * sumXY + sumX2 * sumX4 * sumY - sumX3 * sumX3 * sumY;
      let scale = -count * sumX2Y * sumX3 + count * sumX4 * sumXY + sumX * sumX2 * sumX2Y - sumX * sumX4 * sumY - sumX2 * sumX2 * sumXY + sumX2 * sumX3 * sumY;
      let accel = sumY * sumX * sumX3 - sumY * sumX2 * sumX2 - sumXY * count * sumX3 + sumXY * sumX2 * sumX - sumX2Y * sumX * sumX + sumX2Y * count * sumX2;

      return (x) => (offset + x * scale + x * x * accel) / det;
    },

    /**
     * Triggers file download in a browser
     * @param {string} url Full path to the file
     * @param {string} [filename] Optional filename with extension.
     *   File name will be automatically extracted from URL
     * @returns {Promise}
     */
    downloadFile(url, filename) {
      let deferred = $q.defer();

      try {
        if (!url) {
          return deferred.reject('No URL provided');
        }

        // Detect AWS S3 link from a special path construction
        //  as path contain '{aws_bucket}; filename="{filename_with_extension}"'
        let awsMatch;
        if (!filename) {
          let param = 'filename';
          let regExp = new RegExp('[^.]' + param + '="([^"]+).*$');
          awsMatch = decodeURIComponent(url).match(regExp);

          if (awsMatch && awsMatch[1]) {
            filename = awsMatch[1];
          }
        }

        if (!filename) {
          // Get filename with extension from URL
          filename = url.match(/\/([^?\/]+)(?=\?|$)/)[1];
        }

        if (!filename) {
          return deferred.reject('Unable to extract filename from file URL');
        }

        let elm = document.createElement('a');
        elm.href = url;
        elm.setAttribute('download', filename);
        elm.style.display = 'none';
        document.body.appendChild(elm);
        elm.click();

        setTimeout(function() {
          document.body.removeChild(elm);
          deferred.resolve();
        }, 0);

      } catch (e) {
        deferred.reject(e);
      }

      return deferred.promise;
    },

    /**
     * Format number as Fahrenheit (add °F sign)
     * @param {number} f Fahrenheit
     * @param {number} fractionDigits Number of digits after the decimal point.
     */
    formatAsF(f, fractionDigits = 1) {
      return `${Number(f).toFixed(fractionDigits)} °F`;
    },

    formatAsC(c, fractionDigits = 1) {
      return `${Number(c).toFixed(fractionDigits)} °C`;
    },

    /**
     * Convert Celsius to Fahrenheit
     * @param {number} c Celsius
     * @return {number}
     */
    convertCToF(c) {
      return c * 9 / 5 + 32;
    },

    /**
     * Convert Fahrenheit to Celsius
     * @param {number} f Fahrenheit
     * @return {number} Celsius
     */
    convertFToC(f) {
      return (f - 32) * 5 / 9;
    },

    /**
     * Returns phone number in international format
     * @param {string} phone Number with country code
     * @param {number} [type] Phone type
     * @param {boolean} [hideType] Set true to hide phone type prefix
     * @returns {string}
     */
    formatPhoneNumber(phone, type, hideType) {
      const value = phone && phone.toString().replace(/\[+\(\)\s]/g, '');
      let result = '';
      try {
        let phoneNumber = libphonenumber.parsePhoneNumber('+' + value, {
          defaultCountry: 'US'
        });

        if (phoneNumber.isPossible()) {
          result = phoneNumber.formatInternational();
        } else {
          result = '+' + value;
        }
      } catch (e) {
        result = value || '';
      }
      if (!hideType) {
        let phoneType = type && PHONE_TYPES.find(t => t.id === type);
        return phoneType ?  `${result} (${$t(phoneType.name)})` :  `${result} (${$t('users.phoneTypes.0')})`;
      } else {
        return result;
      }
    },

    /**
     * Returns object with country code and number
     * @param {string} phone
     * @returns {}
     */
    parsePhoneNumber(phone) {
      let result = {
        phoneCode: '',
        phoneNumber: ''
      };

      if (!phone) {
        return result;
      }

      // Remove all non-digit characters
      phone = phone.replace(/\D/g, '');

      try {
        let phoneNumber = libphonenumber.parsePhoneNumber('+' + phone, {
          defaultCountry: 'US'
        });
        if (phoneNumber.isPossible()) {
          result.phoneCode = phoneNumber.countryCallingCode && parseInt(phoneNumber.countryCallingCode);
          result.phoneNumber = this.convertPhoneToNumber(phoneNumber.nationalNumber);
        }
      } catch (e) {
        result.phoneNumber = this.convertPhoneToNumber(phone);
      }

      return result;
    },

    /**
     * Format bytes
     * @param {number} bytes
     * @param {number} decimals
     * @returns {string}
     */
    formatBytes(bytes, decimals = 2) {
      if (!bytes || bytes === 0) {
        return '0 Bytes';
      }

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },

    /**
     * Format trend value by its unit
     * @param {number|string} value
     * @param {string} [unit]
     * @param {boolean} [short]
     * @return {string}
     */
    formatValueByUnit(value, unit, short) {
      if (typeof value === 'string') {
        value = parseFloat(value.replace(',', '.'));
      }
      if (isNaN(value) || !Number.isFinite(value)) {
        return '';
      }

      if (value < 0) {
        // hide negative units
        // NOTE: change this logic if there will be some units with negative numbers
        return '';
      }

      const unitLc = unit?.toLowerCase();

      if (!unit) {
        value = Math.round(value * 100) / 100;
        return value.toString();
      }

      if (unitLc === 'ms') {
        if (value === 0) {
          return '0';
        }
        if (value < 1000) {
          return '';
        }
        if (value < 10 * 60 * 1000) {
          return short
            ? moment.duration(value).format(durationFormats.shortSec, { usePlural: false })
            : moment.duration(value).format(durationFormats.sec, { usePlural: false });
        }
        return short
          ? moment.duration(value).format(durationFormats.shortMin, { usePlural: false })
          : moment.duration(value).format(durationFormats.min, { usePlural: false });
      }

      if (unitLc === 'timestamp_ms') {
        return moment(Math.round(value * 60 * 60 * 1000)).tz('GMT').format('LT');
      }

      if (unitLc === 'min') {
        if (!value) {
          return short ? '0' : '0 min';
        }
        return short
          ? moment.duration(Math.round(value * 60 * 1000)).format(durationFormats.shortMin, { usePlural: false })
          : moment.duration(Math.round(value * 60 * 1000)).format(durationFormats.min, { usePlural: false });
      }

      if (unitLc === 'hour') {
        if (!value) {
          return short ? '0' : '0 hours';
        }
        return short
          ? moment.duration(Math.round(value * 60 * 60 * 1000)).format(durationFormats.shortMin, { usePlural: false })
          : moment.duration(Math.round(value * 60 * 60 * 1000)).format(durationFormats.min, { usePlural: false });
      }

      if (unit === '%') {
        value = Math.round(value);
        return `${value}%`;
      }

      if (unitLc === 'c' || unitLc === 'celsius') {
        value = Math.round(this.convertCToF(value) * 100) / 100;
        return `${value} °F`;
      }

      if (unitLc === 'f' || unitLc === 'fahrenheit') {
        value = Math.round(value * 100) / 100;
        return `${value} °F`;
      }

      if (unitLc === 'rooms') {
        value = Math.round(value * 100) / 100;
        return short ? value.toString() : value === 1 ? `1 transition` : `${value} transitions`;
      }

      if (unitLc === 'falls') {
        value = Math.round(value * 100) / 100;
        return short ? value.toString() : value === 1 ? `1 fall` : `${value} falls`;
      }

      if (unitLc === 'times') {
        value = Math.round(value * 100) / 100;
        return short ? value.toString() : value === 1 ? `1 time` : `${value} times`;
      }

      if (unitLc === 'visits') {
        value = Math.round(value * 100) / 100;
        return short ? value.toString() : value === 1 ? `1 visit` : `${value} visits`;
      }

      if (unitLc === 'stability events' || unitLc === 'events') {
        value = Math.round(value * 100) / 100;
        return short ? value.toString() : value === 1 ? `1 event` : `${value} events`;
      }

      value = Math.round(value * 100) / 100;
      return short ? value.toString() : `${value} ${unit}`;
    },

    /**
     * Convert meters to inches
     * @param {number} meters
     * @param {number} [fractionDigits]
     * @returns {number}
     */
    convertMetersToInches(meters, fractionDigits = 2) {
      const pow = Math.pow(10, fractionDigits);
      return Math.round(meters * 100 * pow / 2.54) / pow;
    },

    /**
     * Convert inches to meters
     * @param {number} inches
     * @param {number} fractionDigits
     * @returns {number}
     */
    convertInchesToMeters(inches, fractionDigits = 2) {
      const pow = Math.pow(10, fractionDigits);
      return Math.round(inches * 2.54 * pow / 100) / pow;
    },

    /**
     * Get location highlights color
     * @param {string} currentColor
     * @param {number} tagPriority
     * @return {string}
     */
    getHighlightColor(currentColor, tagPriority) {
      const highlightColors = config.getConfigByKey('locationHighlightColors') || [];
      const currentHighlightIndex = highlightColors.findIndex(c => c.color === currentColor) || 0;
      const index = (tagPriority || tagPriority === 0) && tagPriority < highlightColors.length ? tagPriority : 0;

      if (!currentColor) {
        return highlightColors[index].color;
      } else {
        return index > currentHighlightIndex ? highlightColors[index].color : currentColor;
      }
    },

  };
}
